/*
 * Web Push for the backoffice: browser side.
 *
 * The API (services/pms/notifications/push) sends every bell notification to
 * the browsers of that vendor (or of the superadmins) that subscribed. This
 * module owns the browser half: permission, the PushManager subscription, and
 * the per-account "I turned this on" flag that lets a returning user be
 * re-subscribed after signing in again without being asked twice.
 */

import { routes } from '../../assets/constants';
import { ApiMethod } from '../models/api-types.type';

type Send = (method: ApiMethod, path: string, body?: any) => Promise<any>;

export type PushStatus =
    | 'unsupported' // no service worker (dev server) or no Push API
    | 'ios-install' // iPhone/iPad in a Safari tab: push only exists once added to the Home Screen
    | 'unavailable' // the API has no VAPID keys configured
    | 'denied' // the user blocked notifications for this site
    | 'off'
    | 'on';

const FLAG_PREFIX = 'ardealy.bo.push.';

function isIos(): boolean {
    const ua = navigator.userAgent;
    // iPadOS reports itself as a Mac; the touch points give it away.
    return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;
}

async function registration(): Promise<ServiceWorkerRegistration | undefined> {
    if (!('serviceWorker' in navigator)) return undefined;
    return navigator.serviceWorker.getRegistration();
}

function hasPushApi(): boolean {
    return 'PushManager' in window && 'Notification' in window;
}

function flagKey(accountKey: string) {
    return `${FLAG_PREFIX}${accountKey}`;
}

function readFlag(accountKey: string): boolean {
    try {
        return localStorage.getItem(flagKey(accountKey)) === '1';
    } catch {
        return false;
    }
}

function writeFlag(accountKey: string, on: boolean) {
    try {
        if (on) localStorage.setItem(flagKey(accountKey), '1');
        else localStorage.removeItem(flagKey(accountKey));
    } catch {
        // Private mode: the user is simply asked again next time.
    }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
    const current = sub.options?.applicationServerKey;
    if (!current) return false;
    const a = new Uint8Array(current);
    return a.length === key.length && a.every((byte, i) => byte === key[i]);
}

type PushConfig = { enabled: boolean; publicKey: string | null };

// Asked once per page load: the bell remounts on every navigation, and the
// answer only changes with a deploy of the API.
let configRequest: Promise<PushConfig> | null = null;

function fetchConfig(send: Send): Promise<PushConfig> {
    if (!configRequest) {
        configRequest = send(ApiMethod.GET, routes.notifications.push.config)
            .then((config: PushConfig) => ({ enabled: !!config?.enabled, publicKey: config?.publicKey || null }))
            .catch(() => {
                configRequest = null; // a failed request is retried next time
                return { enabled: false, publicKey: null };
            });
    }
    return configRequest;
}

/**
 * Subscribes this browser (or refreshes its subscription) and tells the API,
 * with the interface language: the API words each push in it.
 */
async function subscribeWith(send: Send, reg: ServiceWorkerRegistration, publicKey: string, lang: string) {
    const key = urlBase64ToUint8Array(publicKey);
    let sub = await reg.pushManager.getSubscription();
    // Keys rotated on the server: the old subscription can never be sent to.
    if (sub && !sameKey(sub, key)) {
        await sub.unsubscribe().catch(() => undefined);
        sub = null;
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    const { endpoint, keys } = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
    await send(ApiMethod.POST, routes.notifications.push.subscriptions, { endpoint, keys, lang });
}

export async function getPushStatus(send: Send, accountKey: string): Promise<PushStatus> {
    const iosTab = isIos() && !isStandalone();
    if (!iosTab && (!hasPushApi() || !(await registration()))) return 'unsupported';
    const { enabled } = await fetchConfig(send);
    if (!enabled) return 'unavailable';
    if (iosTab) return 'ios-install';
    if (Notification.permission === 'denied') return 'denied';
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    return sub && Notification.permission === 'granted' && readFlag(accountKey) ? 'on' : 'off';
}

/** Asks for permission (this must run from a click) and subscribes. */
export async function enablePush(send: Send, accountKey: string, lang: string): Promise<PushStatus> {
    const reg = await registration();
    if (!reg || !hasPushApi()) return 'unsupported';
    const { enabled, publicKey } = await fetchConfig(send);
    if (!enabled || !publicKey) return 'unavailable';

    const permission = await Notification.requestPermission();
    if (permission === 'denied') return 'denied';
    if (permission !== 'granted') return 'off';

    await subscribeWith(send, reg, publicKey, lang);
    writeFlag(accountKey, true);
    return 'on';
}

export async function disablePush(send: Send, accountKey: string): Promise<PushStatus> {
    writeFlag(accountKey, false);
    const sub = await (await registration())?.pushManager.getSubscription();
    if (sub) {
        await send(ApiMethod.DELETE, routes.notifications.push.subscriptions, { endpoint: sub.endpoint }).catch(() => undefined);
        await sub.unsubscribe().catch(() => undefined);
    }
    return 'off';
}

/**
 * After sign-in: if this account turned push on here before and the browser
 * still allows it, subscribe again. Also keeps the server's copy fresh (the
 * API drops browsers it has not heard from in 60 days) and its language in
 * step with the interface. Never prompts.
 */
export async function resumePush(send: Send, accountKey: string, lang: string): Promise<void> {
    if (!readFlag(accountKey) || !hasPushApi() || Notification.permission !== 'granted') return;
    const reg = await registration();
    if (!reg) return;
    const { enabled, publicKey } = await fetchConfig(send);
    if (!enabled || !publicKey) return;
    await subscribeWith(send, reg, publicKey, lang);
}

/**
 * On sign-out. The browser's subscription is dropped, so the push service
 * answers the API with "gone" and the API deletes its row. Vendor device
 * sessions are also cleared server-side when the session ends; this covers
 * superadmins and a sign-out that never reached the API.
 */
export async function dropPushSubscription(): Promise<void> {
    try {
        const sub = await (await registration())?.pushManager.getSubscription();
        await sub?.unsubscribe();
    } catch {
        // Nothing to clean up.
    }
}
